import * as React from 'react';
import {FormattedMessage} from 'react-intl';

import {IContentBlock} from 'features/blocks/types';
import {TemplateNames} from 'features/template/enums';
import {truncate} from 'features/services/utils/string-helpers';
import {ModalType, useModal} from 'features/modal/hooks/useModal';
import {ImageSizes} from '@organisms/Shell/image-size-enum';
import {useCurrentTemplate} from 'features/template/hooks/useViewModel';
import {useDeviceStore, selectIsTablet} from 'features/ui/store/useDeviceStore';
import {gtmEventsInspirationPageOpen} from 'features/services/googleTagManager/events';
import {getFormattedMessage} from 'features/services/utils/language-helper';

import 'components/blocks/PageLinks/InspirationBlock/style.scss';

import {P} from '@atoms/Text/P';
import {ButtonLink} from '@atoms/LinksAndButtons/ButtonLink';
import {AsyncImage} from '@atoms/Media/AsyncImage';
import {InspirationPage} from "@pages/InspirationPage";
import {EpiImage} from "features/search/types";
import {getPageViewModel} from "@organisms/BuyResidenceGeneral/services/api";
import { ButtonColor, ButtonSize, ButtonType } from '@atoms/LinksAndButtons/Button';
import {Headline} from '@atoms/Text/Headline';
import {HeadlineLevel} from 'features/ui/enums';

interface InspirationBlockType {
    url: string;
    title: string;
    mainBody: string;
    image: EpiImage;
    openInNewWindow?: boolean;
}
type Props = IContentBlock<InspirationBlockType>;

export const InspirationBlock: React.FunctionComponent<Props> = ({ data, id }) => {
    const { open } = useModal();
    const isTablet = useDeviceStore(selectIsTablet);

    const currentTemplate = useCurrentTemplate();

    const openInModal = (url?: string) => async () => {
        if(!url) return;

        const vm = await getPageViewModel(url);
        open(
            <InspirationPage viewModel={vm.template.viewModel} />,
            { fullscreen: true, type: ModalType.INSPIRATION, triggerElementId: 'inspiration-' + id, ariaLabel: getFormattedMessage('jm.components.inspirationModal.ariaLabel') }
        );
    };

    return (
        <article className="inspiration-block" id={id}>
            <div className="inspiration-block__teaser">
                <div className="inspiration-block__img">
                    <AsyncImage
                        key="inspiration-block"
                        className="inspiration-block__img"
                        quality={ImageSizes.SIZE_2560}
                        image={data.image}
                        fullWidth
                    />
                </div>

                <div className="inspiration-block__text">
                    {isTablet
                        ? <Headline level={HeadlineLevel.H3}>{data.title}</Headline>
                        : <Headline level={HeadlineLevel.H2}>{data.title}</Headline>
                    }
                    {data.mainBody && <P>
                        {truncate(data.mainBody, 20)}
                    </P>}
                    {currentTemplate === TemplateNames.BUY_RESIDENCE_INFO
                        ? <ButtonLink
                            href={data.url}
                            onClick={openInModal(data.url)}
                            preventDefault
                            id={'inspiration-' + id}
                            buttonColor={ButtonColor.DEFAULT} 
                            buttonSize={ButtonSize.DEFAULT} 
                            buttonType={ButtonType.GHOST} 
                        >
                            <FormattedMessage id="jm.components.InspirationBlock.readMore" />
                        </ButtonLink>
                        : <ButtonLink
                            href={data.url}
                            attachedEvent={gtmEventsInspirationPageOpen(window.location.pathname)}
                            buttonColor={ButtonColor.DEFAULT} 
                            buttonSize={ButtonSize.DEFAULT} 
                            buttonType={ButtonType.GHOST} 
                        >
                            <FormattedMessage id="jm.components.InspirationBlock.readMore" />
                        </ButtonLink>
                    }
                </div>
            </div>
        </article>
    )
};
